import { useMemo } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { serviceColors } from "../utils/constants";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";
import { findTemplateForServiceCategory } from "../utils/templateToServiceCategory";
import { useGetProviderBasicProfile } from "../custom-hooks/queries";
import {
  getProviderCapabilitiesList,
  getVendorListedServiceCategories,
} from "../utils/vendorProfileCapabilities";

export { getProviderCapabilitiesList, getVendorListedServiceCategories };

function getBadgeColors(category, rfis) {
  const fromRfi = findRfiColorMeta(category, rfis || []);
  if (fromRfi) {
    return {
      bg: fromRfi.bg,
      text: fromRfi.text,
      border: fromRfi.border,
    };
  }
  const preset = serviceColors[category];
  if (preset?.bgColor) {
    return {
      bg: preset.bgColor,
      text: preset.textColor,
      border: preset.borderColor,
    };
  }
  return { bg: "#EEF2FF", text: "#4338CA", border: "#4338CA" };
}

/**
 * Loads vendor profile service categories. Falls back to GET /Providers/{id}/Basic-Profile
 * (same endpoint as vendor detail Overview) when the assessments list omits capabilities.
 */
export function useVendorProfileServiceCategories(provider, providerIdProp) {
  const providerId =
    providerIdProp || provider?.provider_id || provider?.id || null;
  const providerIdStr = providerId != null ? String(providerId) : null;

  const hasEmbeddedCapabilities =
    getProviderCapabilitiesList(provider).length > 0;
  const needsCapabilitiesFetch = !!providerIdStr && !hasEmbeddedCapabilities;

  const { data: basicProfile, isLoading: isLoadingCapabilities } =
    useGetProviderBasicProfile(needsCapabilitiesFetch ? providerIdStr : null);

  const resolvedProvider = useMemo(() => {
    const fromList = getProviderCapabilitiesList(provider);
    const fromBasicProfile = getProviderCapabilitiesList(basicProfile);
    const capabilities = fromList.length > 0 ? fromList : fromBasicProfile;
    if (!provider && capabilities.length === 0) return null;
    if (!provider) {
      return { provider_id: providerId, provider_capabilities: capabilities };
    }
    return { ...provider, provider_capabilities: capabilities };
  }, [provider, basicProfile, providerId]);

  const vendorEntries = useMemo(
    () => getVendorListedServiceCategories(resolvedProvider),
    [resolvedProvider],
  );

  const vendorCategoryNames = useMemo(
    () => vendorEntries.map((e) => e.name),
    [vendorEntries],
  );

  const vendorCategorySet = useMemo(
    () => new Set(vendorCategoryNames),
    [vendorCategoryNames],
  );

  return {
    providerId,
    resolvedProvider,
    vendorEntries,
    vendorCategoryNames,
    vendorCategorySet,
    isLoadingCapabilities,
  };
}

/**
 * Non-binding vendor profile service suggestions for VQQ (templates) or VQA (service categories).
 */
export default function VendorListedServicesSuggestions({
  provider,
  providerId: providerIdProp,
  mode = "vqq",
  rfis = [],
  templateOptions = [],
  selectedTemplates = [],
  onSelectedTemplatesChange,
  v2Options = [],
  selectedCategories = [],
  onSelectedCategoriesChange,
  gridClassName = "sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6",
  contentClassName = "mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]",
}) {
  const {
    providerId,
    vendorEntries,
    vendorCategoryNames,
    isLoadingCapabilities,
  } = useVendorProfileServiceCategories(provider, providerIdProp);

  const categoryToTemplate = useMemo(() => {
    if (mode !== "vqq") return new Map();
    const map = new Map();
    vendorEntries.forEach(({ name, capability }) => {
      const template = findTemplateForServiceCategory(
        name,
        templateOptions,
        capability,
      );
      if (template) map.set(name, template);
    });
    return map;
  }, [mode, vendorEntries, templateOptions]);

  const actionableVqaCategories = useMemo(() => {
    if (mode !== "vqa") return [];
    const validNames = new Set(
      (v2Options || [])
        .filter((o) => !o.isGroupHeader)
        .map((o) => o.value),
    );
    return vendorCategoryNames.filter((n) => validNames.has(n));
  }, [mode, vendorCategoryNames, v2Options]);

  const nameToRfiId = useMemo(() => {
    const map = {};
    (v2Options || []).forEach((o) => {
      if (!o.isGroupHeader && o.rfiId) map[o.value] = o.rfiId;
    });
    return map;
  }, [v2Options]);

  const suggestedTemplateValues = useMemo(
    () => Array.from(categoryToTemplate.values()).map((t) => t.value),
    [categoryToTemplate],
  );

  const allSuggestedTemplatesSelected =
    suggestedTemplateValues.length > 0 &&
    suggestedTemplateValues.every((v) =>
      selectedTemplates.some((t) => t.value === v),
    );

  const allSuggestedCategoriesSelected =
    actionableVqaCategories.length > 0 &&
    actionableVqaCategories.every((name) => {
      const id = nameToRfiId[name];
      return selectedCategories.some(
        (t) => t.value === id || t.label === name,
      );
    });

  const isTemplateSelected = (templateValue) =>
    selectedTemplates.some((t) => t.value === templateValue);

  const isCategorySelected = (categoryName) => {
    const id = nameToRfiId[categoryName];
    return selectedCategories.some(
      (t) => t.value === id || t.label === categoryName,
    );
  };

  const toggleTemplate = (categoryName) => {
    const template = categoryToTemplate.get(categoryName);
    if (!template || !onSelectedTemplatesChange) return;
    if (isTemplateSelected(template.value)) {
      onSelectedTemplatesChange(
        selectedTemplates.filter((t) => t.value !== template.value),
      );
    } else {
      onSelectedTemplatesChange([
        ...selectedTemplates,
        {
          value: template.value,
          label: template.label,
          global: template.global,
          owner_id: template.owner_id,
        },
      ]);
    }
  };

  const toggleCategory = (categoryName) => {
    if (!actionableVqaCategories.includes(categoryName)) return;
    const id = nameToRfiId[categoryName];
    if (!id || !onSelectedCategoriesChange) return;
    if (isCategorySelected(categoryName)) {
      onSelectedCategoriesChange(
        selectedCategories.filter((t) => t.value !== id),
      );
    } else {
      onSelectedCategoriesChange([
        ...selectedCategories,
        { value: id, label: categoryName },
      ]);
    }
  };

  const addAllTemplates = () => {
    if (!onSelectedTemplatesChange || suggestedTemplateValues.length === 0)
      return;
    if (allSuggestedTemplatesSelected) {
      onSelectedTemplatesChange(
        selectedTemplates.filter(
          (t) => !suggestedTemplateValues.includes(t.value),
        ),
      );
    } else {
      const existing = new Set(selectedTemplates.map((t) => t.value));
      const toAdd = Array.from(categoryToTemplate.values()).filter(
        (t) => !existing.has(t.value),
      );
      onSelectedTemplatesChange([
        ...selectedTemplates,
        ...toAdd.map((t) => ({
          value: t.value,
          label: t.label,
          global: t.global,
          owner_id: t.owner_id,
        })),
      ]);
    }
  };

  const addAllCategories = () => {
    if (!onSelectedCategoriesChange || actionableVqaCategories.length === 0)
      return;
    if (allSuggestedCategoriesSelected) {
      const ids = new Set(
        actionableVqaCategories.map((n) => nameToRfiId[n]).filter(Boolean),
      );
      onSelectedCategoriesChange(
        selectedCategories.filter((t) => !ids.has(t.value)),
      );
    } else {
      const existing = new Set(selectedCategories.map((t) => t.value));
      const toAdd = actionableVqaCategories
        .filter((n) => nameToRfiId[n] && !existing.has(nameToRfiId[n]))
        .map((n) => ({ value: nameToRfiId[n], label: n }));
      onSelectedCategoriesChange([...selectedCategories, ...toAdd]);
    }
  };

  const helperText =
    mode === "vqq"
      ? "Categories listed on this vendor's profile. These are suggestions only — you may request any template below."
      : "Categories listed on this vendor's profile. These are suggestions only — you may request any service category below.";

  const emptyText =
    mode === "vqq"
      ? "This vendor hasn't listed service categories on their profile. All templates remain available."
      : "This vendor hasn't listed service categories on their profile. All categories remain available.";

  const bulkLabel = mode === "vqq"
    ? allSuggestedTemplatesSelected
      ? "Remove all suggested"
      : "Add all as templates"
    : allSuggestedCategoriesSelected
      ? "Remove all suggested"
      : "Add all as service categories";

  const showBulk =
    mode === "vqq"
      ? suggestedTemplateValues.length > 0
      : actionableVqaCategories.length > 0;

  if (!providerId && !provider) return null;

  return (
    <div className={gridClassName}>
      <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
        Vendor&apos;s Listed Services
      </label>
      <div className={contentClassName}>
        {isLoadingCapabilities ? (
          <p className="text-sm text-gray-500 italic sm:pt-1.5">
            Loading vendor services...
          </p>
        ) : vendorCategoryNames.length > 0 ? (
          <>
            <p className="text-xs text-gray-500 mb-2">{helperText}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {vendorCategoryNames.map((category) => {
                const colors = getBadgeColors(category, rfis);
                const clickable =
                  mode === "vqq"
                    ? categoryToTemplate.has(category)
                    : actionableVqaCategories.includes(category);
                const isSelected =
                  mode === "vqq"
                    ? categoryToTemplate.has(category) &&
                      isTemplateSelected(categoryToTemplate.get(category).value)
                    : isCategorySelected(category);
                const title =
                  mode === "vqq"
                    ? clickable
                      ? isSelected
                        ? `Remove "${categoryToTemplate.get(category).label}" from selection`
                        : `Add "${categoryToTemplate.get(category).label}" to selection`
                      : "No matching VQQ template available for this category"
                    : clickable
                      ? isSelected
                        ? `Remove "${category}" from scope`
                        : `Add "${category}" to scope`
                      : "Not available in the service category list";

                return (
                  <button
                    key={category}
                    type="button"
                    disabled={!clickable}
                    onClick={() =>
                      mode === "vqq"
                        ? toggleTemplate(category)
                        : toggleCategory(category)
                    }
                    title={title}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset transition-opacity ${
                      clickable
                        ? "cursor-pointer hover:opacity-80"
                        : "cursor-default opacity-70"
                    } ${isSelected ? "ring-2 ring-offset-1" : ""}`}
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      borderColor: colors.border,
                      "--tw-ring-color": `${colors.border}33`,
                    }}
                  >
                    {isSelected && (
                      <CheckIcon className="h-3 w-3" aria-hidden="true" />
                    )}
                    {category}
                  </button>
                );
              })}
              {showBulk && (
                <button
                  type="button"
                  onClick={
                    mode === "vqq" ? addAllTemplates : addAllCategories
                  }
                  className="ml-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
                >
                  {bulkLabel}
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 italic sm:pt-1.5">{emptyText}</p>
        )}
      </div>
    </div>
  );
}
